import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useTheme } from "@mui/material"
import fp from "lodash/fp"

import { useLazyPowerlineQuery } from "features/api"
import type {
  PowerlinePlotDef,
  PlotView,
  LinePlotView,
  TableStatPlotView,
} from "types/dashboard.types"
import { selectCurrentOrgId } from "features/store/orgSlice"
import type { LabelAlign } from "types/viewProperties.types"
import { axisToFormatterProps, getFormatter } from "widgets/plots/configuration/helpers"
import { parseSeasonalAmpacity } from "helpers/utils/seasonalAmpacity"

export function useNominalAmpacityViews(def: PowerlinePlotDef, powerlineId?: number) {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage
  const theme = useTheme()
  const orgId = useSelector(selectCurrentOrgId)
  const [getPowerline] = useLazyPowerlineQuery()

  const [viewWExtras, setViewWExtras] = useState<PlotView>(def.view)

  const isAmpacityLinePlot = useMemo(
    () => def.view.nameKey === "LONG_TERM_AMPACITY" && def.view.type === "xy",
    [def],
  )

  const isBelowNominalTable = useMemo(
    () => def.view.nameKey === "BELOW_NOMINAL_AMPACITY" && def.view.type === "table-stat",
    [def],
  )

  useEffect(() => {
    if ((!isAmpacityLinePlot && !isBelowNominalTable) || !powerlineId) {
      setViewWExtras(def.view)
      return
    }

    const fetchData = async () => {
      try {
        const powerline = await getPowerline(
          { id: powerlineId, params: { org_id: Number(orgId) } },
          true,
        ).unwrap()

        const seasons = parseSeasonalAmpacity(powerline.ampacity_seasonal)
        const hasSeasonal = seasons.length > 0

        if (!hasSeasonal && powerline.ampacity_nominal == null) {
          setViewWExtras(def.view)
          return
        }

        if (isBelowNominalTable) {
          const viewAsTable = def.view as TableStatPlotView
          const newView = fp.merge(viewAsTable, {
            properties: hasSeasonal
              ? { seasonalThreshold: powerline.ampacity_seasonal }
              : { threshold: powerline.ampacity_nominal },
          })
          setViewWExtras(newView)
          return
        }

        const viewAsXY = def.view as LinePlotView
        const formatter = getFormatter(
          axisToFormatterProps(viewAsXY.properties.axes.y, viewAsXY.properties, locale),
        )

        const labelStyle = {
          fill: theme.palette.text.secondary,
          fontSize: "0.7rem",
          paintOrder: "stroke",
          stroke: theme.palette.background.paper,
          strokeWidth: 3,
        }
        const lineStyle = {
          strokeDasharray: "4 4",
          strokeWidth: 0.75,
          stroke: theme.palette.text.secondary,
          shapeRendering: "geometricPrecision",
        }

        if (hasSeasonal) {
          const values = seasons.map((season) => season.value)
          const valueLabels = fp.flow(
            fp.uniq,
            fp.map((value: number) => [value, formatter(value)] as [number, string]),
            fp.fromPairs,
          )(values) as Record<number, string>

          const newView = fp.merge(viewAsXY, {
            properties: {
              axes: {
                y: {
                  bounds: [`${Math.min(...values)}`, `${Math.max(...values)}`],
                },
              },
              yReferenceStepLine: {
                seasonal: powerline.ampacity_seasonal,
                valueLabels,
                label: t("plots_references.AMPACITY_NOMINAL"),
                labelStyle,
                lineStyle,
              },
            },
          })
          setViewWExtras(newView)
          return
        }

        const formattedLabel = `${t("plots_references.AMPACITY_NOMINAL")} ${formatter(
          powerline.ampacity_nominal,
        )}`

        const newView = fp.merge(viewAsXY, {
          properties: {
            axes: {
              y: {
                bounds: [
                  `${powerline.ampacity_nominal}`,
                  `${powerline.ampacity_nominal}`,
                ],
              },
            },
            yReferenceLines: [
              ...(viewAsXY.properties?.yReferenceLines ?? []),
              {
                y: powerline.ampacity_nominal,
                label: formattedLabel,
                labelAlign: "start" as LabelAlign,
                labelStyle,
                lineStyle,
              },
            ],
          },
        })
        setViewWExtras(newView)
      } catch {
        setViewWExtras(def.view)
      }
    }

    fetchData()
  }, [
    isAmpacityLinePlot,
    isBelowNominalTable,
    powerlineId,
    orgId,
    t,
    locale,
    def.view,
    getPowerline,
    theme.palette,
  ])

  return {
    view: viewWExtras,
  }
}
